import type { IPartyState } from '../../../interface/state/IParty';
import { isAllPartiesList, type PartyListSource } from '../../../lib/ballot-parties-api';

export interface ICode {
	code: string;
	name: string;
	codeId: number;
	status: boolean;
}
export interface IChildCode {
	code: string;
	name: string;
	codeId: number;
	parent: number;
	status: boolean;
}

export interface IVoteCount {
	partyId: number;
	partyName: string;
	votes: number;
	label: string;
	logo: string;
	longName?: string;
}
export interface IPartyVotes {
	id: number;
	name: string;
	code: string;
	votes: IVoteCount[];
}

/**
 * The 0-vote rows for one entity. Copies exactly these six fields, so anything extra on
 * the party (e.g. a ballot's `ballotOrder`) can never reach the submitted payload.
 */
export const seedVoteRows = (parties: readonly IPartyState[]): IVoteCount[] =>
	parties.map((party) => ({
		partyId: party.id,
		votes: 0,
		label: party.shortName,
		logo: party.logo,
		partyName: party.shortName,
		longName: party.longName,
	}));

/**
 * Re-seed an entity's existing rows against a freshly resolved party list. Entered votes
 * are kept; parties new to the list arrive at 0; an off-list row is kept only while it
 * carries votes (never silently drop a count). Returns `existing` itself when nothing
 * changed, so re-seeding an up-to-date entity does not re-render.
 */
export const reconcileVoteRows = (existing: IVoteCount[], parties: readonly IPartyState[]): IVoteCount[] => {
	const byId = new Map(existing.map((row) => [row.partyId, row]));
	const listed = new Set(parties.map((p) => p.id));
	const next = seedVoteRows(parties).map((seed) => {
		const row = byId.get(seed.partyId);
		return row ? { ...seed, votes: row.votes } : seed;
	});
	const kept = existing.filter((row) => !listed.has(row.partyId) && (parseInt(row.votes + '') || 0) > 0);
	const merged = [...next, ...kept];
	const unchanged =
		merged.length === existing.length &&
		merged.every((row) => {
			const old = byId.get(row.partyId);
			return (
				!!old &&
				old.votes === row.votes &&
				old.label === row.label &&
				old.logo === row.logo &&
				old.partyName === row.partyName &&
				old.longName === row.longName
			);
		});
	return unchanged ? existing : merged;
};

export type ursType =
	| 'Electoral Division'
	| 'Upload Result'
	| 'Upload Result Status'
	| 'Result Summary'
	| 'Party Vote Count'
	| 'Preview'
	| 'Party Vote Summary'
	| 'Polling Agents'
	| 'Election Result Submission Status';

/**
 * Sheet order. The EC8A prints parties alphabetically by acronym, so ascending `label`
 * (shortName) reproduces its S/N column and the agent can run straight down the paper.
 * Never re-sort by votes or by answer.
 */
const byLabel = (a: Pick<IVoteCount, 'label'>, b: Pick<IVoteCount, 'label'>) => {
	if (a.label > b.label) return 1;
	if (a.label < b.label) return -1;
	return 0;
};

export const sortBySheetOrder = (votes: readonly IVoteCount[]): IVoteCount[] => [...votes].sort(byLabel);

/**
 * Where the seeded party list came from, plus each party's ballot position. Kept beside
 * `partyVotes` rather than on the vote rows so the submitted payload shape never changes.
 * Keyed by partyId (a plain object so it survives the JSON round-trip of a draft).
 */
export interface IPartyBallot {
	source: PartyListSource;
	order: Record<string, number | null>;
}

export const defaultPartyBallot: IPartyBallot = { source: 'none', order: {} };

/** True when the list is the election's real ballot ('election' / 'attribute'). */
export const isBallotOrdered = (ballot: IPartyBallot | undefined): boolean =>
	!!ballot && ballot.source !== 'none' && !isAllPartiesList(ballot.source);

/**
 * Display order for a party list on every capture step. A real ballot shows in
 * ballotOrder (unordered parties last, label as tiebreak) so the screen matches the
 * paper; the all-parties fallback keeps alphabetical sheet order exactly.
 */
export const sortForDisplay = <T extends Pick<IVoteCount, 'partyId' | 'label'>>(
	votes: readonly T[],
	ballot: IPartyBallot | undefined
): T[] => {
	if (!isBallotOrdered(ballot)) return [...votes].sort(byLabel);
	const position = (partyId: number) => {
		const o = ballot!.order[String(partyId)];
		return typeof o === 'number' ? o : Infinity;
	};
	return [...votes].sort((a, b) => {
		const ao = position(a.partyId);
		const bo = position(b.partyId);
		if (ao !== bo) return ao < bo ? -1 : 1;
		return byLabel(a, b);
	});
};

export type stageEnumType = 'Child' | 'Party Votes';

export interface IResultSummary {
	DataVotersOnRegister: number; // done
	DataAccreditedVoters: number; // done
	DataBallotPapersIssuedToPoolingUnit: number; // done
	DataUnusedBallotPapers: number; // done
	DataRejectedBallot: number; // done
	DataTotalValidVotes: number; // done
	DataTotalUsedBallotPapers: number; // done
}

export const defaultSummary: IResultSummary = {
	DataVotersOnRegister: 0,
	DataAccreditedVoters: 0, // done
	DataBallotPapersIssuedToPoolingUnit: 0, // done
	DataUnusedBallotPapers: 0, // done
	DataRejectedBallot: 0, // done
	DataTotalValidVotes: 0, // done
	DataTotalUsedBallotPapers: 0, // done
};
